import {useMemo, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {ChevronLeftIcon} from '@heroicons/react/outline';
import {Spinner} from '@tyrannosaurustech/ui';

import {ContentWrapper} from '../../../components/ContentWrapper';
import {TitleM, ParagraphS} from '../../../components/atoms/Typography';
import {InitialsAvatar} from '../../../components/atoms/Avatar';
import {Theme} from '../../../components/atoms/theme';
import {useGetAuditLogsQuery, useGetAuditStatsQuery} from '../../../redux/reduxQuery/auditApi';
import {useGetUserSilentQuery} from '../../../redux/reduxQuery/usersApi';
import {
  BUCKETS,
  EVENT_LABELS,
  RANGE_OPTIONS,
  UserStats,
  bucketOfEvent,
  computeWindow,
  fmtDateTime,
  fmtDayShort,
  summarize,
} from './constants';
import {ChartStyles, DeltaPill, HBarChart, Legend, Sparkline, StackedDailyChart} from './charts';
import {RecordCell} from './logDisplay';

const initialsOf = (name: string) =>
  (name || '?')
    .split(' ')
    .map((s) => s.charAt(0))
    .slice(0, 2)
    .join('');

export default function UserActivity() {
  const {userId} = useParams();
  const [rangeDays, setRangeDays] = useState(14);
  const [eventFilter, setEventFilter] = useState<string>('all');
  const [selectedDay, setSelectedDay] = useState<number | null>(null);

  const win = useMemo(() => computeWindow(rangeDays), [rangeDays]);
  const {data, isLoading} = useGetAuditStatsQuery({
    startDate: win.startDate,
    endDate: win.endDate,
  });
  const {data: prevData} = useGetAuditStatsQuery({
    startDate: win.prevStartDate,
    endDate: win.prevEndDate,
  });
  const {data: userRecord} = useGetUserSilentQuery(userId || '', {
    skip: !userId,
  });
  const logQuery = useMemo(() => {
    if (selectedDay === null) return `userId=${userId}&take=25`;
    const dayKey = win.dayList[selectedDay];
    const start = `${dayKey}T00:00:00.000Z`;
    const end = `${dayKey}T23:59:59.999Z`;
    return `userId=${userId}&startDate=${encodeURIComponent(start)}&endDate=${encodeURIComponent(end)}&take=200`;
  }, [userId, selectedDay, win.dayList]);
  const {data: logData, isLoading: logsLoading} = useGetAuditLogsQuery(logQuery);

  const stats: UserStats | undefined = useMemo(() => (data?.users ?? []).find((u: UserStats) => u.userId === userId), [data, userId]);
  const prevStats: UserStats | undefined = useMemo(
    () => (prevData?.users ?? []).find((u: UserStats) => u.userId === userId),
    [prevData, userId],
  );

  const summary = useMemo(() => (stats ? summarize(stats, win.dayList) : null), [stats, win.dayList]);
  const prevSummary = useMemo(() => (prevStats ? summarize(prevStats, win.dayList) : null), [prevStats, win.dayList]);

  const displayName = stats?.userName || (userRecord as any)?.name || 'Unknown user';
  const email = (userRecord as any)?.email || '';
  const roles = (userRecord as any)?.roles || '';

  const logs = useMemo(() => {
    const rows = logData?.auditLogs ?? [];
    return eventFilter === 'all' ? rows : rows.filter((r: any) => bucketOfEvent(r.eventType).key === eventFilter);
  }, [logData, eventFilter]);

  return (
    <ContentWrapper>
      <ChartStyles />
      <div className="pt-6 pb-3">
        <Link
          to="/admin/team-activity"
          className="inline-flex items-center gap-1 text-sm font-semibold text-gray-500 hover:text-primary"
          style={{textDecoration: 'none'}}
        >
          <ChevronLeftIcon width={15} />
          Team Activity
        </Link>
      </div>

      <div className="mb-5 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex items-center gap-4">
          <InitialsAvatar
            text={initialsOf(displayName)}
            background={Theme.colors.primaries_000}
            hover={Theme.colors.primaries_000}
            size={56}
          />
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <TitleM style={{margin: 0, fontSize: 20}}>{displayName}</TitleM>
              {roles && (
                <span
                  className="rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide"
                  style={{
                    background: '#F7E9FB',
                    color: Theme.colors.primaries_000,
                  }}
                >
                  {String(roles).split(',')[0]}
                </span>
              )}
            </div>
            {email && <ParagraphS style={{margin: '2px 0 0', color: '#6C6579'}}>{email}</ParagraphS>}
          </div>
        </div>
        <div className="flex items-center gap-5">
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-wide text-gray-400">Last active</div>
            <div className="mt-0.5 text-sm font-semibold">{fmtDateTime(stats?.lastActive || '')}</div>
          </div>
          <div className="flex rounded-lg border border-gray-300 bg-white p-[3px]">
            {RANGE_OPTIONS.map((d) => (
              <button
                key={d}
                onClick={() => {
                  setRangeDays(d);
                  setSelectedDay(null);
                }}
                aria-pressed={rangeDays === d}
                className="rounded-md px-3 py-1 text-xs font-semibold"
                style={
                  rangeDays === d
                    ? {
                        background: '#F7E9FB',
                        color: Theme.colors.primaries_000,
                      }
                    : {color: '#6C6579'}
                }
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-24">
          <Spinner colorClassName="text-primary" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 pb-5 lg:grid-cols-5">
            <div className="ta-fadeup rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-gray-400">
                <span className="h-2 w-2 rounded-sm" style={{background: Theme.colors.primaries_000}} />
                Total actions
              </div>
              <div className="mt-1 text-2xl font-bold tabular-nums">{stats?.total || 0}</div>
              <div className="mt-2 flex items-center justify-between gap-2">
                <DeltaPill current={stats?.total || 0} prev={prevStats?.total || 0} />
                <Sparkline
                  values={summary?.dailyTotals || win.dayList.map(() => 0)}
                  color={Theme.colors.primaries_000}
                  width={72}
                  height={22}
                />
              </div>
            </div>
            {BUCKETS.filter((b) => b.key !== 'other').map((b, bi) => (
              <div
                key={b.key}
                className="ta-fadeup rounded-xl border border-gray-200 bg-white p-4"
                style={{animationDelay: `${(bi + 1) * 60}ms`}}
              >
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-gray-400">
                  <span className="h-2 w-2 rounded-sm" style={{background: b.color}} />
                  {b.label}
                </div>
                <div className="mt-1 text-2xl font-bold tabular-nums">{summary?.totalsByBucket[b.key] || 0}</div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <DeltaPill current={summary?.totalsByBucket[b.key] || 0} prev={prevSummary?.totalsByBucket[b.key] || 0} />
                  <Sparkline values={summary?.dailyByBucket[b.key] || win.dayList.map(() => 0)} color={b.color} width={72} height={22} />
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-4 pb-5 xl:grid-cols-[1.55fr_1fr]">
            <div className="order-1 ta-card rounded-xl border border-gray-200 bg-white p-5">
              <div className="mb-1 text-sm font-bold">Daily activity</div>
              <ParagraphS style={{margin: '0 0 10px', color: '#6C6579'}}>
                Actions per day by type, {fmtDayShort(win.dayList[0])} – {fmtDayShort(win.dayList[win.dayList.length - 1])}, click a day to
                inspect it
              </ParagraphS>
              <div className="mb-3">
                <Legend
                  items={BUCKETS.map((b) => ({
                    label: b.label,
                    color: b.color,
                  }))}
                />
              </div>
              <StackedDailyChart
                dayLabels={win.dayList.map(fmtDayShort)}
                series={BUCKETS.map((b) => ({
                  key: b.key,
                  label: b.label,
                  color: b.color,
                  values: summary?.dailyByBucket[b.key] || win.dayList.map(() => 0),
                }))}
                selected={selectedDay}
                onSelectDay={setSelectedDay}
              />
            </div>

            {/* On smaller displays this sits directly under Daily activity (order-2), so the
                click-a-day → filtered-log connection stays visible without "Work by record type"
                breaking up the two. At xl+ it drops back below both, spanning the full width. */}
            <div className="order-2 xl:order-3 xl:col-span-2 rounded-xl border border-gray-200 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 pb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold">
                      {selectedDay !== null ? `Activity on ${fmtDayShort(win.dayList[selectedDay])}` : 'Recent activity'}
                    </span>
                    {selectedDay !== null && (
                      <button
                        onClick={() => setSelectedDay(null)}
                        className="flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold"
                        style={{
                          background: '#F7E9FB',
                          color: Theme.colors.primaries_000,
                        }}
                        title="Back to most recent events"
                      >
                        ✕ Clear day
                      </button>
                    )}
                  </div>
                  <ParagraphS style={{margin: '2px 0 0', color: '#6C6579'}}>
                    {selectedDay !== null
                      ? `${logs.length} event${logs.length === 1 ? '' : 's'} that day`
                      : `${logs.length} most recent event${logs.length === 1 ? '' : 's'}, newest first`}
                  </ParagraphS>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {[{key: 'all', label: 'All', color: ''} as any].concat(BUCKETS).map((b) => (
                    <button
                      key={b.key}
                      onClick={() => setEventFilter(b.key)}
                      aria-pressed={eventFilter === b.key}
                      className="flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold"
                      style={
                        eventFilter === b.key
                          ? {
                              borderColor: Theme.colors.primaries_000,
                              background: '#F7E9FB',
                              color: Theme.colors.primaries_000,
                            }
                          : {borderColor: '#D4CCE0', color: '#6C6579'}
                      }
                    >
                      {b.color && <span className="h-2 w-2 rounded-full" style={{background: b.color}} />}
                      {b.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-[10.5px] font-bold uppercase tracking-wider text-gray-400">
                      <th className="px-4 py-2">When</th>
                      <th className="px-4 py-2">Action</th>
                      <th className="px-4 py-2">Record</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logsLoading && (
                      <tr>
                        <td colSpan={3} className="px-4 py-10 text-center text-sm text-gray-400">
                          Loading…
                        </td>
                      </tr>
                    )}
                    {!logsLoading && logs.length === 0 && (
                      <tr>
                        <td colSpan={3} className="px-4 py-10 text-center text-sm text-gray-400">
                          No matching events.
                        </td>
                      </tr>
                    )}
                    {logs.map((log: any) => {
                      const bucket = bucketOfEvent(log.eventType);
                      return (
                        <tr key={log.id} className="ta-row border-b border-gray-100 last:border-0 hover:bg-gray-50">
                          <td className="whitespace-nowrap px-4 py-2.5 text-xs text-gray-500">{fmtDateTime(log.createdAt)}</td>
                          <td className="whitespace-nowrap px-4 py-2.5">
                            <span className="flex items-center gap-2 text-xs font-semibold">
                              <span className="h-2 w-2 rounded-full" style={{background: bucket.color}} />
                              {EVENT_LABELS[log.eventType] || log.eventType}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-2.5">
                            <RecordCell log={log} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="order-3 xl:order-2 ta-card rounded-xl border border-gray-200 bg-white p-5">
              <div className="mb-1 text-sm font-bold">Work by record type</div>
              <ParagraphS style={{margin: '0 0 10px', color: '#6C6579'}}>Which records these actions touched</ParagraphS>
              {summary && summary.totalsByEntity.length > 0 ? (
                <HBarChart items={summary.totalsByEntity.slice(0, 6)} />
              ) : (
                <div className="py-10 text-center text-sm text-gray-400">No activity in this window.</div>
              )}
            </div>
          </div>

          <ParagraphS style={{color: '#9A93A8', margin: '14px 2px 32px'}}>
            Every create, edit, status change, note, and deletion is attributed to the user who made it. Full history is in{' '}
            <Link to="/admin/audit-logs" style={{color: Theme.colors.primaries_000}}>
              Audit Logs
            </Link>
            .
          </ParagraphS>
        </>
      )}
    </ContentWrapper>
  );
}
